//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { IScope } from 'angular';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { hasBVImportLabel } from '@legacy-components/common/universal-image-actions/checkItemRightsStatus';
import Cuid from '@models/Cuid';
import PartialCuid from '@models/PartialCuid';
import { RightsCreationSource } from '@models/RightsCreationSource';
import makeAngularReactControllerWithScope from '@react-bridge/angular-react-widget-with-scope';
import { getClientId } from '@redux/selectors/getClientId';
import { MediaTrackingContextProvider } from '@tracker/mediaTracking/contexts/MediaTrackingContextProvider';
import { useGetMediaTrackingSource } from '@tracker/mediaTracking/useGetMediaTrackingSource';
import { UnexpectedErrorAlert } from '@uia/UnexpectedErrorAlert';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { UIAContainer } from '@uia/layout/UIAContainer';
import { getLaunchOptions } from '@uia/redux/uiaSelector';
import { UIARequestRights } from '@uia/rights/UIARequestRightsForm';
import { UIARequestStatus } from '@uia/rights/UIARequestStatus';
import { UIARightsMarkedApproved } from '@uia/rights/UIARightsMarkedApproved';
import { UIARightsOptions } from '@uia/rights/UIARightsOptions';
import { UIARightsOwned } from '@uia/rights/UIARightsOwned';
import { UIARightsSkipped } from '@uia/rights/UIARightsSkipped';
import { isDefined, isUndefined } from '@utils/filterPredicates';

import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';

interface Props {
  onCloseModal: () => void;
  onUpdateRights: () => void;
  onRightsSubOptionSelected?: (option: string) => void;
  shouldShowRightsOptions?: boolean;
}

const UIARightsFlow = ({
  onCloseModal,
  onRightsSubOptionSelected,
  onUpdateRights,
  shouldShowRightsOptions = true,
}: Props) => {
  const clientId = useSelector(getClientId);
  const source = useGetMediaTrackingSource();

  const launchOptions = useSelector(getLaunchOptions);

  const cuid =
    isDefined(launchOptions?.itemId) && isDefined(launchOptions.itemType)
      ? Cuid.fromPartial(
          PartialCuid.build({
            id: launchOptions.itemId,
            itemType: launchOptions.itemType,
          }),
          clientId,
        )
      : undefined;

  const {
    data: metadata,
    isError: isErrorMetadata,
    isLoading: isLoadingMetadata,
  } = useMediaMetadataQuery(cuid);

  const isBVImport = metadata && hasBVImportLabel(metadata);

  if (isLoadingMetadata) {
    return <LoadingSpinner className='unison-component' />;
  }

  if (isErrorMetadata) {
    return (
      <UIAContainer>
        <UnexpectedErrorAlert />
      </UIAContainer>
    );
  }

  if (RIGHTS_STATUS.BRAND_OWNED === metadata.rightsRequestStatus.status) {
    return (
      <UIAContainer>
        <UIARightsOwned onClose={() => onCloseModal()} />
      </UIAContainer>
    );
  } else if (
    // if the request is APPROVED and creationSource is RightsCreationSource.ApprovedRightsRequest
    // we need to show the rights request that was approved and not only UIARequestStatus
    RIGHTS_STATUS.APPROVED === metadata.rightsRequestStatus.status &&
    RightsCreationSource.ApprovedRightsRequest ===
      metadata.rightsPermissions?.creationSource
  ) {
    return (
      <UIAContainer>
        <UIARequestRights
          cuid={metadata.rootCuid}
          onCloseModal={onCloseModal}
        />
      </UIAContainer>
    );
  } else if (
    isDefined(metadata.rightsPermissions?.creationSource) &&
    [
      RightsCreationSource.RelationshipClientToClient,
      RightsCreationSource.RelationshipInfluencerToClient,
    ].includes(metadata.rightsPermissions.creationSource) &&
    !metadata.rightsPermissions.isRightsExpired
  ) {
    return (
      <UIAContainer>
        <UIARequestStatus
          isBVImport={isBVImport}
          onCloseModal={onCloseModal}
          source={metadata.rightsPermissions.creationSource}
          // passing "approved" as the status instead of data.rightsRequestStatus.status which is returning "unsent" for RelationshipClientToClient
          status={RIGHTS_STATUS.APPROVED}
        />
      </UIAContainer>
    );
  } else if (metadata.rightsPermissions?.isRightsSkipped) {
    if (!hasBVImportLabel(metadata)) {
      return (
        <UIAContainer>
          <UIARightsSkipped onClose={() => onCloseModal()} />
        </UIAContainer>
      );
    } else {
      return (
        <UIAContainer>
          <UIARequestStatus
            isBVImport={true}
            onCloseModal={onCloseModal}
            source={RightsCreationSource.SkippedRightsRequest}
            // BV imports are automatically approved
            status={RIGHTS_STATUS.APPROVED}
          />
        </UIAContainer>
      );
    }
  } else if (
    isDefined(metadata.rightsPermissions) &&
    metadata.rightsPermissions.creationSource ===
      RightsCreationSource.ManuallySet &&
    !metadata.rightsPermissions.isRightsExpired
  ) {
    return (
      <UIAContainer>
        <MediaTrackingContextProvider source={source}>
          <UIARightsMarkedApproved
            expirationTime={metadata.rightsPermissions.expirationTime}
            onClose={() => onCloseModal()}
            onUpdateRights={onUpdateRights}
            partialCuid={Cuid.toPartial(metadata.rootCuid)}
          />
        </MediaTrackingContextProvider>
      </UIAContainer>
    );
  }

  return (
    <UIAContainer>
      <MediaTrackingContextProvider source={source}>
        <UIARightsOptions
          cuid={metadata.rootCuid}
          onCloseModal={onCloseModal}
          onRightsSubOptionSelected={(option: string) => {
            if (onRightsSubOptionSelected) {
              onRightsSubOptionSelected(option);
            }
          }}
          onUpdateRights={onUpdateRights}
          shouldShowOptions={shouldShowRightsOptions}
          socialNetwork={metadata.socialNetwork}
        />
      </MediaTrackingContextProvider>
    </UIAContainer>
  );
};

angular.module('curalate').component('uiaRightsFlow', {
  bindings: {
    shouldShowRightsOptions: '<',
  },
  controller: makeAngularReactControllerWithScope(
    (scope: { $ctrl: Props } & IScope) => {
      return (
        <UIARightsFlow
          onCloseModal={() => scope.$emit('uia:modal:close')}
          onRightsSubOptionSelected={(option: string) => {
            scope.$emit('uia:rights:sub-option-selected', option);
          }}
          onUpdateRights={() => {
            scope.$emit('uia:rights-permission-changed');
          }}
          shouldShowRightsOptions={scope.$ctrl.shouldShowRightsOptions}
        />
      );
    },
  ),
});

const LoadingSpinner = styled(CenteredSpinner)(
  ({ theme }) => `
    margin-top: ${theme.spacings[7]};
  `,
);
